import _find from 'lodash/find';
import _some from 'lodash/some';
import _get from 'lodash/get';
import _has from 'lodash/has';
import { Value, Values } from '@lib/interfaces/form';
import {
  AttachmentCuInputObject,
  AttachmentFileInputObject,
  Documentation,
  DocumentationValidationStatusEnum,
  FormAnswerCreateInputObject,
  FormAnswerDocumentStatusEnum,
  FormAnswerDocumentStatusReasonEnum,
  FormAnswerRespGroupEnum,
  FormTemplateQuestionField,
  FormTemplateQuestionFieldOption,
  FormTemplateQuestionFieldsKindEnum,
  FormTemplateVersion,
} from 'graphql-common';
import { DocumentReadRecord } from 'components/TaskCompletionForm/DocumentQuestionField';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import {
  dateFormatForApi,
  dateTimePickerDateFormatForApi,
} from '@lib/enums/dateTime';

function extractQuestionAndFieldId(key: string) {
  const match = key.match(/question-([a-z0-9\\-]+)-field-([a-z0-9\\-]+)/);
  if (match) {
    return { questionId: match[1], fieldId: match[2] };
  }
  return null;
}

// Define separate functions for each kind of question field processing
const processInputTextArea = (fieldValue: Value) =>
  fieldValue ? [{ value: `${fieldValue}` }] : undefined;

const processCheckboxRadioButtonSingleSelectDecisionBox = (
  fieldValue: Value,
  options: FormTemplateQuestionFieldOption[],
) =>
  options.map(({ id: optionId, values: optionValues }) => {
    let selected = false;
    if (Array.isArray(fieldValue) && fieldValue.length) {
      selected = _some(fieldValue, { id: optionId });
    } else if (
      fieldValue &&
      typeof fieldValue === 'object' &&
      Object.prototype.hasOwnProperty.call(fieldValue, 'value')
    ) {
      selected = optionValues.some(
        (item) => item === _get(fieldValue, 'value'),
      );
    }
    return { optionId, selected };
  });

const processImportantNotice = (options: FormTemplateQuestionFieldOption[]) =>
  options.map(({ id: optionId }) => ({ optionId }));

const processRating = (fieldValue: Value) =>
  _has(fieldValue, 'value')
    ? [{ value: `${_get(fieldValue, 'value')}` }]
    : undefined;

const processNumberInput = (fieldValue: Value) =>
  fieldValue
    ? [{ value: `${parseFloat(`${fieldValue}`).toFixed(1)}` }]
    : undefined;

const processMinMax = (fieldValue: Value) => {
  const v1 = _get(fieldValue, 0);
  const v2 = _get(fieldValue, 1);
  return [
    {
      value:
        v1 === undefined ? undefined : `${parseFloat(v1 as string).toFixed(1)}`,
      group: FormAnswerRespGroupEnum.Min,
    },
    {
      value:
        v1 === undefined ? undefined : `${parseFloat(v2 as string).toFixed(1)}`,
      group: FormAnswerRespGroupEnum.Max,
    },
  ];
};

const processDate = (fieldValue: Value) =>
  fieldValue
    ? [{ value: getFormattedDate(fieldValue as string, dateFormatForApi) }]
    : undefined;

const processDateRange = (fieldValue: Value) => {
  const v1 = _get(fieldValue, 0);
  const v2 = _get(fieldValue, 1);
  return [
    {
      value:
        v1 === undefined
          ? undefined
          : getFormattedDate(v1 as string, dateFormatForApi),
      group: FormAnswerRespGroupEnum.StartDate,
    },
    {
      value:
        v2 === undefined
          ? undefined
          : getFormattedDate(v2 as string, dateFormatForApi),
      group: FormAnswerRespGroupEnum.EndDate,
    },
  ];
};

const processTime = (fieldValue: Value) =>
  fieldValue ? [{ value: `${fieldValue}` }] : undefined;

const processTimeRange = (fieldValue: Value) => {
  const v1 = _get(fieldValue, 0);
  const v2 = _get(fieldValue, 1);
  return [
    {
      value: v1 === undefined ? undefined : `${v1}`,
      group: FormAnswerRespGroupEnum.StartTime,
    },
    {
      value: v2 === undefined ? undefined : `${v2}`,
      group: FormAnswerRespGroupEnum.EndTime,
    },
  ];
};

const processDatetime = (fieldValue: Value) =>
  fieldValue
    ? [
        {
          value: getFormattedDate(
            fieldValue as string,
            dateTimePickerDateFormatForApi,
          ),
        },
      ]
    : undefined;

const processYesNo = (fieldValue: Value, options: any[]) =>
  options.map(({ id: optionId, group }) => ({
    optionId,
    selected: _get(fieldValue, 'value') === group,
    group,
  }));

/**
 * Build resp entries for a DOCUMENT field.
 *
 * Per BE spec (DAT-1509):
 * - READ            — user marked-as-read in this session (has a fresh
 *                     `documentationVersionViewId`, plus the explicit
 *                     `documentationVersionMarkAsRead` confirmation).
 * - UNAVAILABLE     — doc not returned by id_in query (DELETED), not
 *                     validated (NOT_VALIDATED), offline (OFFLINE); FREE_PLAN
 *                     is BE-verified when ids are stripped. NO_ACCESS is
 *                     BE-verified against the operator's own access level —
 *                     an operator on `DOCUMENTATION_MANAGEMENT: LIMITED_READ`
 *                     cannot open task documents at all, so this is the only
 *                     honest answer for them (never UNREAD).
 * - READ_PREVIOUSLY — `Documentation.read === true` (BE returns true iff the
 *                     current user has explicitly confirmed a version
 *                     `>= lastRequiredRereadVersionNumber` via
 *                     `documentationVersionMarkAsRead`). Emitted when the
 *                     user did not click in this session but the BE already
 *                     has compliance.
 * - UNREAD          — default fallback.
 */
const processDocument = (
  field: FormTemplateQuestionField,
  docsById: Record<string, Documentation>,
  readRecords: Record<string, DocumentReadRecord>,
  isOnline: boolean,
  isViewLocked: boolean,
  emittedViewIds: Set<string>,
) =>
  (field.options || []).map((opt) => {
    const optionId = opt.id;

    // The operator's `DOCUMENTATION_MANAGEMENT` access is below READ (either
    // because the company is on the free plan, or because a paid-plan admin
    // restricted this operator specifically): they cannot open task documents
    // at all, so optionDocumentation isn't even sent from the BE (skipped by
    // @skip(if: $isViewLocked)), and NO_ACCESS is the only status reason BE
    // will accept — never UNREAD, and never FREE_PLAN (BE verifies that
    // against the company's actual plan, which may not be free).
    if (isViewLocked) {
      return {
        optionId,
        status: FormAnswerDocumentStatusEnum.Unavailable,
        statusReason: FormAnswerDocumentStatusReasonEnum.NoAccess,
      };
    }

    const docId = opt.optionDocumentation?.documentationId;

    if (!docId) {
      // Malformed option without a doc id — treat as UNAVAILABLE/DELETED.
      return {
        optionId,
        status: FormAnswerDocumentStatusEnum.Unavailable,
        statusReason: FormAnswerDocumentStatusReasonEnum.Deleted,
      };
    }

    const sessionRead = readRecords[docId];
    if (sessionRead) {
      // BE rejects the same `documentationVersionViewId` across multiple
      // FormAnswerResp rows in one submission. First occurrence carries the
      // view id; later occurrences degrade to READ_PREVIOUSLY (the user did
      // read it in this session — just not specifically for this answer).
      if (emittedViewIds.has(sessionRead.versionViewId)) {
        return {
          optionId,
          status: FormAnswerDocumentStatusEnum.ReadPreviously,
        };
      }
      emittedViewIds.add(sessionRead.versionViewId);
      return {
        optionId,
        status: FormAnswerDocumentStatusEnum.Read,
        documentationVersionViewId: sessionRead.versionViewId,
      };
    }

    const doc = docsById[docId];
    if (!doc) {
      // Doc isn't in our local map. Online: could be deleted, access-restricted,
      // plan-stripped, or the per-field query hasn't settled yet — we cannot
      // distinguish, so don't assert DELETED (BE strictly verifies it). Default
      // to UNREAD (always safe, no extra fields, BE doesn't verify). Required-
      // to-read Yup validation gates submit elsewhere. Offline: trust OFFLINE.
      if (!isOnline) {
        return {
          optionId,
          status: FormAnswerDocumentStatusEnum.Unavailable,
          statusReason: FormAnswerDocumentStatusReasonEnum.Offline,
        };
      }
      return {
        optionId,
        status: FormAnswerDocumentStatusEnum.Unread,
      };
    }

    if (doc.validationStatus !== DocumentationValidationStatusEnum.Validated) {
      return {
        optionId,
        status: FormAnswerDocumentStatusEnum.Unavailable,
        statusReason: FormAnswerDocumentStatusReasonEnum.NotValidated,
      };
    }

    if (doc.read === true) {
      return {
        optionId,
        status: FormAnswerDocumentStatusEnum.ReadPreviously,
      };
    }

    return {
      optionId,
      status: FormAnswerDocumentStatusEnum.Unread,
    };
  });

const processAttachment = (
  fieldValue: Value,
  attachmentsAttached: AttachmentCuInputObject[],
) => {
  if (!fieldValue) return [];
  if (Array.isArray(fieldValue) && fieldValue.length) {
    fieldValue.forEach((fieldValueItem, index) => {
      const filename = _get(fieldValueItem, 'filename');
      const file = _get(
        fieldValueItem,
        'file',
      ) as unknown as AttachmentFileInputObject;
      if (file) attachmentsAttached.push({ file, filename, position: index });
    });
  }
  return [];
};

interface DocumentContext {
  docsByFieldId: Record<string, Record<string, Documentation>>;
  readRecords: Record<string, DocumentReadRecord>;
  isOnline: boolean;
  isViewLocked: boolean;
}

export default function getAnswers(
  values: Values,
  formTemplateVersion: FormTemplateVersion,
  documentContext?: DocumentContext,
) {
  const { questions } = formTemplateVersion;
  const answers: FormAnswerCreateInputObject[] = [];

  const documentFieldsEmitted = new Set<string>();

  // DOCUMENT fields are option-driven: they don't appear in `values` because no
  // form input is rendered for them. Emit their resp directly from the template.
  if (documentContext) {
    const emittedViewIds = new Set<string>();
    questions.forEach((question) => {
      question.fields?.forEach((field) => {
        if (field.kind !== FormTemplateQuestionFieldsKindEnum.Document) return;
        const resp = processDocument(
          field,
          documentContext.docsByFieldId[field.id] || {},
          documentContext.readRecords,
          documentContext.isOnline,
          documentContext.isViewLocked,
          emittedViewIds,
        );
        answers.push({
          questionId: question.id,
          fieldId: field.id,
          resp,
        });
        documentFieldsEmitted.add(`${question.id}:${field.id}`);
      });
    });
  }

  Object.keys(values).forEach((key) => {
    const ids = extractQuestionAndFieldId(key);
    const fieldValue = values[key] as Value;
    if (!ids) return;
    const attachmentsAttached: AttachmentCuInputObject[] = [];
    const { questionId, fieldId } = ids;
    const question = _find(questions, { id: questionId });
    const field = _find(question?.fields, { id: fieldId });
    if (!field) return;
    // Skip DOCUMENT fields — already emitted above from template walk.
    if (documentFieldsEmitted.has(`${questionId}:${fieldId}`)) return;

    let resp;
    switch (field.kind) {
      case FormTemplateQuestionFieldsKindEnum.Input:
      case FormTemplateQuestionFieldsKindEnum.TextArea:
        resp = processInputTextArea(fieldValue);
        break;
      case FormTemplateQuestionFieldsKindEnum.Checkbox:
      case FormTemplateQuestionFieldsKindEnum.RadioButton:
      case FormTemplateQuestionFieldsKindEnum.SingleSelect:
      case FormTemplateQuestionFieldsKindEnum.DecisionBox:
      case FormTemplateQuestionFieldsKindEnum.Checklist:
        resp = processCheckboxRadioButtonSingleSelectDecisionBox(
          fieldValue,
          field.options || [],
        );
        break;
      case FormTemplateQuestionFieldsKindEnum.CheckboxButtonGrid:
        resp = fieldValue;
        break;
      case FormTemplateQuestionFieldsKindEnum.Rating:
        resp = processRating(fieldValue);
        break;
      case FormTemplateQuestionFieldsKindEnum.MinMax:
        resp = processMinMax(fieldValue);
        break;
      case FormTemplateQuestionFieldsKindEnum.NumberInput:
        resp = processNumberInput(fieldValue);
        break;
      case FormTemplateQuestionFieldsKindEnum.Date:
        resp = processDate(fieldValue);
        break;
      case FormTemplateQuestionFieldsKindEnum.DateRange:
        resp = processDateRange(fieldValue);
        break;
      case FormTemplateQuestionFieldsKindEnum.Time:
        resp = processTime(fieldValue);
        break;
      case FormTemplateQuestionFieldsKindEnum.TimeRange:
        resp = processTimeRange(fieldValue);
        break;
      case FormTemplateQuestionFieldsKindEnum.Datetime:
        resp = processDatetime(fieldValue);
        break;
      case FormTemplateQuestionFieldsKindEnum.YesNo:
        resp = processYesNo(fieldValue, field.options || []);
        break;
      case FormTemplateQuestionFieldsKindEnum.Attachment:
        resp = processAttachment(fieldValue, attachmentsAttached);
        break;
      case FormTemplateQuestionFieldsKindEnum.ImportantNotice:
        resp = processImportantNotice(field.options || []);
        break;
      default:
        resp = undefined;
        break;
    }
    answers.push({
      questionId,
      fieldId,
      resp: resp || [],
      attachmentsAttached: attachmentsAttached.length
        ? attachmentsAttached
        : undefined,
    });
  });

  return answers;
}
